/* What screens the phone app has, what they are called, and what is
 * remembered between visits.
 *
 * The screens are the six dashboards the design carries plus home. They are
 * deliberately fewer than the desktop portal's rail: Profitability, the
 * global trade boards, the data loads and the raw tables are all pages you
 * work in rather than read, and none of them survives a 390px column. A
 * phone is for the morning look at the mill; the desktop portal is still
 * one tap away in the drawer.
 */

export type ScreenId =
  | 'home' | 'production' | 'stock' | 'cotton' | 'purchases' | 'sales' | 'workforce'

export type Role = 'owner' | 'manager'

export const TITLES: Record<ScreenId, string> = {
  home: 'Home',
  production: 'Production',
  stock: 'Stock report',
  cotton: 'Cotton rates',
  purchases: 'Cotton purchases',
  sales: 'Yarn & waste sales',
  workforce: 'Workforce attendance',
}

/** The drawer, grouped as the desktop portal's rail groups them. */
export const GROUPS: { name: string; items: ScreenId[] }[] = [
  { name: '', items: ['home'] },
  { name: 'Production', items: ['production', 'stock'] },
  { name: 'Marketing', items: ['cotton'] },
  { name: 'Procurement', items: ['purchases'] },
  { name: 'Sales', items: ['sales'] },
  { name: 'HR', items: ['workforce'] },
]

/** Each screen's path under /m. Home is /m itself. */
export const PATHS: Record<ScreenId, string> = {
  home: '/m',
  production: '/m/production',
  stock: '/m/stock',
  cotton: '/m/cotton',
  purchases: '/m/purchases',
  sales: '/m/sales',
  workforce: '/m/workforce',
}

export const screenOf = (pathname: string): ScreenId => {
  const hit = (Object.keys(PATHS) as ScreenId[])
    .filter((id) => id !== 'home')
    .find((id) => pathname === PATHS[id] || pathname.startsWith(PATHS[id] + '/'))
  return hit ?? 'home'
}

// ------------------------------------------------------------- remembered
const KEY = 'millMobile'

export interface Saved {
  role: Role
  /** Set when someone asks for the desktop portal from a phone, so the
   *  redirect does not drag them straight back. */
  desktop?: boolean
}

export function readSaved(): Saved {
  try {
    const raw = JSON.parse(window.localStorage.getItem(KEY) || '{}')
    return { role: raw.role === 'manager' ? 'manager' : 'owner', desktop: !!raw.desktop }
  } catch {
    return { role: 'owner' }
  }
}

export function writeSaved(patch: Partial<Saved>) {
  try {
    window.localStorage.setItem(KEY, JSON.stringify({ ...readSaved(), ...patch }))
  } catch { /* private mode */ }
}
